import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  MultiSelect,
  PaginationComponent,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { useCurrentPage } from '@folio/hooks/useCurrentPage';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type DistributionRequestStatus,
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListChildBrokerageFirmsQueryOptions,
} from 'korint-sdk';
import { useKorintProductConfig } from 'korint-sdk/react';
import { SearchIcon } from 'lucide-react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { IntermediaryListRow } from './IntermediaryListRow';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const PAGE_SIZE = 10;

interface IntermediaryListProps {
  /**
   * The title to be displayed in the module.
   */
  title: string;

  /**
   * The statuses to be displayed in the module.
   */
  viewType: 'approved' | 'refused' | 'waiting';
}

const distributionRequestViewTypes = {
  approved: {
    statuses: ['APPROVED'],
    slug: 'approved-distribution-requests',
  },
  refused: {
    statuses: ['DENIED', 'REVOKED'],
    slug: 'refused-distribution-requests',
  },
  waiting: {
    statuses: ['PENDING', 'WAITING_FOR_SIGNATURE'],
    slug: 'waiting-distribution-requests',
  },
} satisfies Record<
  IntermediaryListProps['viewType'],
  {
    statuses: Array<DistributionRequestStatus>;
    slug: string;
  }
>;

export function IntermediaryList({ title, viewType }: IntermediaryListProps) {
  const { t } = useTranslation();
  const viewProps = distributionRequestViewTypes[viewType];

  const [searchParams, setSearchParams] = useSearchParams();

  const page = useCurrentPage(viewProps.slug);

  const { productsConfig } = useKorintProductConfig();
  const options = useMemo(
    () =>
      Object.entries(productsConfig).map(([productId]) => ({
        value: productId,
        label: t(`products.${productId}.label`),
      })),
    [productsConfig, t],
  );

  // Derive productIds from URL params
  const productIds = useMemo(() => {
    const urlProductIds = searchParams.getAll('productIds');
    if (urlProductIds.length === 0 || options.length === 0) return [];
    return urlProductIds
      .map(id => options.find(opt => opt.value === id))
      .filter((opt): opt is { value: string; label: string } => opt !== undefined);
  }, [searchParams, options]);

  const search = searchParams.get('search') ?? '';
  const orderBy = searchParams.get('orderBy') ?? 'protocolSignedAt';
  const orderDirection = (searchParams.get('orderDirection') ?? 'desc') as 'asc' | 'desc';
  const productFilterMode = (searchParams.get('productFilterMode') ?? 'or') as 'and' | 'or';

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(prev => {
        prev.set('search', value);
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const [inputValue, setInputValue] = useState(search);

  // Update input value when search prop changes (e.g., from URL)
  useEffect(() => {
    setInputValue(search);
  }, [search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(inputValue);
  };

  const handleSearchClear = () => {
    setInputValue('');
    setSearch('');
  };

  const handleSort = useCallback(
    (columnName: string) => {
      setSearchParams(prev => {
        const currentOrderBy = prev.get('orderBy');
        const currentOrderDirection = prev.get('orderDirection') ?? 'asc';

        if (currentOrderBy === columnName) {
          // Three-state cycle: asc -> desc -> no sort -> asc
          if (currentOrderDirection === 'asc') {
            prev.set('orderDirection', 'desc');
          } else if (currentOrderDirection === 'desc') {
            // Remove sorting (reset to default)
            prev.delete('orderBy');
            prev.delete('orderDirection');
          }
        } else {
          // Set new column and default to ascending
          prev.set('orderBy', columnName);
          prev.set('orderDirection', 'asc');
        }
        prev.set('page', '1'); // Reset to first page when sorting
        return prev;
      });
    },
    [setSearchParams],
  );

  // Handler to update productIds in URL
  const setProductIds = useCallback(
    (newProductIds: Array<{ value: string; label: string }>) => {
      setSearchParams(prev => {
        prev.delete('productIds');
        newProductIds.forEach(id => {
          prev.append('productIds', id.value);
        });
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const { data: user, status: userStatus } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: childBrokerageFirms, status } = useQuery({
    ...viewSpecificControllerListChildBrokerageFirmsQueryOptions(user?.brokerageFirmId!, {
      hasSomeDistributionRequestWithStatus: productIds.length > 0 ? undefined : viewProps.statuses,
      productIds: productIds.length > 0 ? productIds.map(v => v.value) : undefined,
      productFilterMode: productIds.length > 0 ? productFilterMode : undefined,
      queryString: search || undefined,
      order: {
        by: orderBy,
        direction: orderDirection,
      },
      count: PAGE_SIZE,
      page,
    }),
    enabled: !!user?.brokerageFirmId,
    placeholderData: keepPreviousData,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-4">
          <span>{title}</span>
          {options.length > 1 && viewType === 'approved' && (
            <Group wrap gap={3} className="mb-4">
              <div className="min-w-64 max-w-md w-full">
                <MultiSelect
                  options={options}
                  onChange={v => setProductIds(v.map(value => value))}
                  value={productIds}
                  placeholder={t('intermediaryTabs.productFilterPlaceholder')}
                  maxMenuHeight={300}
                />
              </div>
              {productIds.length > 1 && (
                <Group gap={2} className="border rounded-md p-1">
                  <Button
                    type="button"
                    variant={productFilterMode === 'or' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setSearchParams(prev => {
                        prev.set('productFilterMode', 'or');
                        prev.set('page', '1');
                        return prev;
                      });
                    }}
                    className="h-8"
                  >
                    {t('intermediaryTabs.productFilterMode.or')}
                  </Button>
                  <Button
                    type="button"
                    variant={productFilterMode === 'and' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setSearchParams(prev => {
                        prev.set('productFilterMode', 'and');
                        prev.set('page', '1');
                        return prev;
                      });
                    }}
                    className="h-8"
                  >
                    {t('intermediaryTabs.productFilterMode.and')}
                  </Button>
                </Group>
              )}
            </Group>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSearchSubmit} className="mb-4">
          <Group align="stretch" gap={2}>
            <div className="w-full">
              <Input
                icon={<SearchIcon />}
                placeholder={t('intermediaryTabs.searchPlaceholder')}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onClear={handleSearchClear}
                clearable
                aria-describedby="search-help-text"
              />
              <Text size="xs" tone="muted" id="search-help-text" className="mt-1">
                {t('intermediaryTabs.searchHelp')}
              </Text>
            </div>
            <Button type="submit" className="mb-0">
              {t('intermediaryTabs.searchButton')}
            </Button>
          </Group>
        </form>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort('companyInfo.name')}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                  title={t('intermediaryTabs.sortHint')}
                >
                  {t('intermediaryTabs.columns.name')}
                  {orderBy === 'companyInfo.name' ? (
                    orderDirection === 'asc' ? (
                      <ArrowUp className="h-4 w-4" />
                    ) : (
                      <ArrowDown className="h-4 w-4" />
                    )
                  ) : (
                    <ArrowUpDown className="h-4 w-4 opacity-50" />
                  )}
                </button>
              </TableHead>
              <TableHead>{t('intermediaryTabs.columns.createdAt')}</TableHead>
              {options.length > 1 && (
                <TableHead>{t('intermediaryTabs.columns.products')}</TableHead>
              )}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {status === 'pending' || userStatus === 'pending' ? (
              <>
                {Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                  //biome-ignore lint/suspicious/noArrayIndexKey: is ok
                  <LoadingRow key={`loading-${idx}`} />
                ))}
              </>
            ) : status === 'error' || userStatus === 'error' ? (
              <ErrorRow />
            ) : (
              childBrokerageFirms.data.map(childBrokerageFirm => (
                <IntermediaryListRow
                  key={childBrokerageFirm.id}
                  brokerageFirm={childBrokerageFirm}
                />
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter>
        <PaginationComponent
          totalData={childBrokerageFirms?.totalCount ?? 0}
          PAGE_SIZE={PAGE_SIZE}
          isLoading={status === 'pending' || userStatus === 'pending'}
          slug={viewProps.slug}
        />
      </CardFooter>
    </Card>
  );
}
